import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Text } from '@/components/base';
import Icon, { IconName } from '@/components/base/Icon';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { I18nResource } from '@/i18n/i18n-supported-language';
import { toClassNames } from '@/utils';
import { createEnumTypeGuard } from '@/utils/validator';

interface Props {
  displayFlagOnly?: boolean;
  className?: string;
  withArrow?: boolean;
}

export interface LanguageDropdownOption {
  text: string;
  value: I18nResource;
  svgIconUrl: string;
}

export const languageOptions: LanguageDropdownOption[] = [
  {
    value: I18nResource.TH_TH,
    text: 'ภาษาไทย',
    svgIconUrl: '/assets/images/flags/th.svg',
  },
  {
    value: I18nResource.EN_US,
    text: 'English',
    svgIconUrl: '/assets/images/flags/us.svg',
  },
  {
    value: I18nResource.JA_JP,
    text: '日本語',
    svgIconUrl: '/assets/images/flags/jp.svg',
  },
  {
    value: I18nResource.KO_KR,
    text: '한국어',
    svgIconUrl: '/assets/images/flags/kr.svg',
  },
  {
    value: I18nResource.TR_TR,
    text: 'Türkçe',
    svgIconUrl: '/assets/images/flags/tr.svg',
  },
  {
    value: I18nResource.HI_IN,
    text: 'हिन्दी',
    svgIconUrl: '/assets/images/flags/in.svg',
  },
];

export const LanguageDropdown: FC<Props> = ({ displayFlagOnly, className, withArrow }) => {
  const { t } = useTranslation('common');

  const { language, changeLanguage } = useAppLanguage();

  const onChangeLanguage = (value: string) => {
    const isLanguage = createEnumTypeGuard(I18nResource)(value);
    if (isLanguage) {
      changeLanguage(value);
    }
  };

  const selectedLanguage = languageOptions.find((option) => option.value === language);

  return (
    <Listbox onChange={onChangeLanguage} value={language}>
      <ListboxButton
        className={toClassNames(
          'relative flex items-center',
          'rounded-lg border-2 border-gray-200 bg-white px-3 py-1',
          displayFlagOnly && 'px-2 py-1.5',
          withArrow && 'gap-2',
          className
        )}
      >
        {selectedLanguage ? (
          <>
            <Image className="w-5" src={selectedLanguage.svgIconUrl} alt={selectedLanguage.text} />
            {!displayFlagOnly && (
              <Text version="revamp-v1" className={toClassNames(!withArrow && 'ml-2')}>
                {selectedLanguage.text}
              </Text>
            )}
            {(withArrow || !displayFlagOnly) && <Icon icon={IconName.ChevronDown} size="16px" />}
          </>
        ) : (
          <Text version="revamp-v1">{t('eddy_sidebar.setting_language_placeholder')}</Text>
        )}
      </ListboxButton>
      <ListboxOptions
        anchor="top end"
        className="absolute -mt-px flex flex-col gap-y-2 rounded-lg border-2 border-gray-200 bg-white px-3 py-2"
      >
        {languageOptions.map((language) => (
          <ListboxOption
            key={language.value}
            value={language.value}
            className={toClassNames('flex items-center gap-2 text-gray-500', 'cursor-pointer', 'font-fustat')}
          >
            {({ selected }) => (
              <>
                <Image src={language.svgIconUrl} alt={language.text} className="h-3" />
                {language.text}
                {selected && <Icon icon={IconName.Check} size="16px" />}
              </>
            )}
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
};
